import { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { deleteMicrogrid } from "../api/microgridsApi";

// The toast for a failed delete. It always carries the HTTP status, so a
// failure can be matched to the sim's logs; 401 and 403 get the same
// wording the Microgrids page uses for an expired session or missing access.
export function describeDeleteMicrogridError(err) {
  const status = err?.status;
  if (status === 401) {
    return "Failed to delete microgrid (401): your session has expired. Please sign out and sign back in.";
  }
  if (status === 403) {
    return "Failed to delete microgrid (403): you do not have permission to delete this microgrid.";
  }
  if (status) {
    return `Failed to delete microgrid (${status}): ${err.message}`;
  }
  return `Failed to delete microgrid: ${err?.message || "no response from the server"}`;
}

export default function useDeleteMicrogrid({
  storageAccountName,
  accessToken,
  userId,
  onDeleted,
}) {
  // The microgrid row the confirmation is open for, or null when closed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Blocks a second confirm that lands before the disabled button re-renders,
  // and a close while the request is in flight.
  const inFlightRef = useRef(false);

  const openDelete = useCallback((design) => {
    if (inFlightRef.current) return;
    setPendingDelete(design);
  }, []);

  const closeDelete = useCallback(() => {
    if (inFlightRef.current) return;
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsDeleting(true);

    const design = pendingDelete;
    try {
      await deleteMicrogrid({
        storageAccountName,
        microgridName: design.name,
        accessToken,
        userId,
      });
      toast.success(`Deleted microgrid "${design.displayName || design.name}".`);
      setPendingDelete(null);
      onDeleted?.(design);
    } catch (err) {
      // Nothing changes on failure: the modal stays open and the list keeps
      // the row, so the user can retry or cancel.
      console.error("[useDeleteMicrogrid] Delete failed:", err);
      toast.error(describeDeleteMicrogridError(err));
    } finally {
      inFlightRef.current = false;
      setIsDeleting(false);
    }
  }, [pendingDelete, storageAccountName, accessToken, userId, onDeleted]);

  return {
    pendingDelete,
    isDeleting,
    openDelete,
    closeDelete,
    confirmDelete,
  };
}
